import * as Sentry from "@sentry/browser";
import { APP_VERSION } from "./default-category.service";
/**
 * A singleton that inits sentry
 */
declare global {
  interface Window {
    dataLayer: any[];
  }
}
class TrackingService {
  public static instance: TrackingService;
  private initialized: boolean = false;

  constructor() {
    if (TrackingService.instance) {
      return TrackingService.instance;
    }

    TrackingService.instance = this;

    if (!window.dataLayer) {
      window.dataLayer = [];
    }

    // Attach the service to dataLayer
    (window.dataLayer as any).trackingService = this;
  }

  public init(): void {
    if (this.initialized) {
      return;
    }
    try {
      Sentry.init({
        dsn: process.env.SENTRY_DSN,
        environment: "production",
        tracesSampleRate: 0.2,
        replaysSessionSampleRate: 0.2,
        replaysOnErrorSampleRate: 1.0,
        beforeSend: (event) => {
          if (event.level !== "error") {
            return event;
          }
          const additional = event.contexts?.additional;
          // app_version because I want to get only the exceptions that I catch
          if (additional && additional.app_version) {
            return event;
          }

          return null;
        },
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
          }),
          Sentry.feedbackIntegration(),
        ],
      });
      this.initialized = true;
      this.setTag("app_version", APP_VERSION);
    } catch (error) {
      console.error("Failed to initialize Sentry:", error);
    }
  }

  public captureException(
    error: Error,
    context?: Record<string, any>
  ): string | undefined {
    if (!this.initialized) {
      console.warn("Sentry not initialized. Call init() first");
      return undefined;
    }

    try {
      return Sentry.withScope((scope) => {
        if (context) {
          scope.setContext("additional", {
            ...context,
            app_version: APP_VERSION,
          });
        }
        return Sentry.captureException(error);
      });
    } catch (e) {
      console.error("Failed to capture exception:", e);
      return undefined;
    }
  }

  public captureMessage(
    message: string,
    level?: Sentry.SeverityLevel
  ): string | undefined {
    if (!this.initialized) {
      console.warn("Sentry not initialized. Call init() first");
      return undefined;
    }

    try {
      return Sentry.captureMessage(message, level);
    } catch (e) {
      console.error("Failed to capture message:", e);
      return undefined;
    }
  }

  public setTag(key: string, value: string): void {
    if (!this.initialized) {
      console.warn("Sentry not initialized. Call init() first");
      return;
    }

    try {
      Sentry.setTag(key, value);
    } catch (error) {
      console.error("Failed to set tag:", error);
    }
  }
}

export const trackingService = new TrackingService();
